import styled from '@emotion/styled';
import { Dropdown, Button, Tag, Popover, List } from 'antd';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { ReactComponent as IconStore } from 'components/StoreSelector/assets/icon_store.svg';
import Icon from '@ant-design/icons';
import { css } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import StoreSelectorMain from './Main';
import { StoreSelectorMainProps } from './interface';
import { CountryFlagIcon } from 'components/CountryFlagIcon';
import { useGetRegionLabel } from 'hooks/business/useRegion';

interface Props extends Omit<StoreSelectorMainProps, 'onClose' | 'visible'> {
  disabled?: boolean;
  className?: string;
  displayTriggerIcon?: boolean;
  defaultTriggerText?: ReactNode;
  displayNodeRender?: (displayNode: ReactNode) => ReactNode;
}

const Popup = styled(Dropdown)`
  flex: 1;
  align-items: center;
  display: flex;

  .store-selector-placeholder {
    color: #515359;
    height: 40px;
    padding: 10px 8px;
    display: flex;
    align-items: center;
    .anticon {
      margin-left: 4px;
      display: flex;
      align-items: center;
    }
    svg {
      width: 20px;
      height: 20px;
    }
    &:hover,
    &.active {
      color: #3370ff;
    }
  }
  .ant-tag {
    padding: 4px 6px;
    line-height: 18px;
    height: 26px;
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    margin: 0 0 0 6px;
    .anticon {
      flex: none;
    }
  }
  .store-selector-default-display {
    max-width: 110px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    text-transform: capitalize;
    &.active {
      color: #3370ff;
    }
  }
`;
const previewStorePopoverCls = css`
  width: 200px;
  overflow: hidden;
  padding: 0;
  .ant-popover-inner-content {
    padding: 8px;
  }
`;
const previewStoreCls = css`
  max-height: 130px;
  overflow: auto;
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background-color: hsla(0, 0%, 70.6%, 0);
    opacity: 0;
  }
  &:hover::-webkit-scrollbar-thumb {
    background-color: hsla(0, 0%, 70.6%, 0.5);
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  .ant-list-items {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .item {
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 4px;
    .anticon {
      align-self: flex-start;
      height: 22px;
      display: flex;
      align-items: center;
      .svg {
        width: 16px;
        height: 11px;
      }
    }
    .name {
      flex: 1;
      word-wrap: break-word;
      white-space: pre-wrap;
      word-break: break-all;
      overflow: hidden;
    }
  }
`;

const StoreSelector = React.memo((props: Props) => {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const {
    disabled,
    className,
    displayTriggerIcon = true,
    defaultTriggerText = '',
    displayNodeRender = (displayNode) => <div>{displayNode}</div>,
    ...mainProps
  } = props;
  const { value, onChange, platformLimit } = mainProps;
  const { config } = useStoreNameConfig();
  const getDisplayText = useCallback(() => {
    const regionTrans = value?.region ? getRegionLabel(value.region) : t('storeSelector.AllStore');
    const displayPlatform = value?.platform || platformLimit;
    const platformTrans = displayPlatform
      ? platformLabelMap[toLowerCase(displayPlatform)]
      : undefined;
    if (value?.follower) {
      return `${platformTrans || t('storeSelector.平台')}-${t('storeSelector.Followed')}`;
    }
    if (value?.platform) {
      return `${platformTrans}-${regionTrans}`;
    }
    if (value?.region) {
      return `${platformTrans || t('storeSelector.平台')}-${regionTrans}`;
    }
    if (value?.storeList?.length) {
      const firstStore = value?.storeList?.[0];
      const name =
        isStoreAliasFirst(config, firstStore?.externalType, firstStore?.region) && firstStore?.alias
          ? firstStore.alias
          : firstStore?.storeName || '';
      const text =
        (value?.storeList?.length || 0) > 1
          ? t('storeSelector.已选{x}家店铺', { x: value?.storeList?.length ?? 0 })
          : name;
      return text;
    }
    return '';
  }, [config, t, value, platformLimit, getRegionLabel]);
  const displayNode = useMemo(() => {
    const text = getDisplayText();
    if (text) {
      return (
        <>
          <Popover
            overlayClassName={previewStorePopoverCls}
            visible={value?.storeList?.length ? undefined : false}
            zIndex={1051}
            content={
              <List
                dataSource={value?.storeList}
                rowKey="storeId"
                className={previewStoreCls}
                renderItem={(item) => (
                  <div className="item">
                    <CountryFlagIcon region={item.region} />
                    <div className="name">
                      <div>
                        {isStoreAliasFirst(config, item?.externalType, item?.region) && item?.alias
                          ? item.alias
                          : item.storeName}
                      </div>
                    </div>
                  </div>
                )}
              />
            }
          >
            <Tag closable onClose={() => onChange?.({})}>
              <span className="store-selector-default-display">{text}</span>
            </Tag>
          </Popover>
          {displayTriggerIcon && (
            <Icon
              component={IconStore}
              className="store-selector-placeholder active"
              onClick={(e) => {
                e.stopPropagation();
                setVisible(true);
              }}
            />
          )}
        </>
      );
    }
    return (
      <Button
        type="link"
        className={`store-selector-placeholder ${visible ? 'active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setVisible(true);
        }}
      >
        {defaultTriggerText}
        {displayTriggerIcon && <Icon component={IconStore} />}
      </Button>
    );
  }, [
    getDisplayText,
    value?.storeList,
    onChange,
    config,
    visible,
    displayTriggerIcon,
    defaultTriggerText,
  ]);
  return (
    <Popup
      disabled={disabled}
      className={className}
      overlay={
        <StoreSelectorMain {...mainProps} visible={visible} onClose={() => setVisible(false)} />
      }
      trigger={['click']}
      visible={visible}
      onVisibleChange={setVisible}
    >
      {displayNodeRender(displayNode)}
    </Popup>
  );
});

export default StoreSelector;
